<script lang="ts" setup>
import { vElementVisibility } from '@vueuse/components'

defineProps({
  levelToOpen: {
    default: 2,
    type: Number,
  },
  loading: {
    default: false,
    type: Boolean,
  },
  queryAttributes: {
    default: () => [],
    type: Array as PropType<ApiAttribute[]>,
  },
  response: {
    default: undefined,
    type: [Array, Object] as PropType<ApiResponseResponse>,
  },
  responseAttributes: {
    default: () => [],
    type: Array as PropType<ApiAttribute[]>,
  },
})

const emit = defineEmits<{
  visibilityChange: [visibility: boolean]
}>()

const { t } = useI18n()

function onVisibilityChange(visibility: boolean) {
  if (visibility) {
    emit('visibilityChange', visibility)
  }
}
</script>

<template>
  <div
    v-element-visibility="onVisibilityChange"
    class="not-prose grid scroll-m-40 grid-cols-1 gap-6 text-lg"
    :class="[
      queryAttributes.length || $slots.left ? 'xl:grid-cols-2' : '',
    ]"
  >
    <div
      v-if="queryAttributes.length || $slots.left"
      class="order-2 flex flex-col gap-6 xl:order-1"
    >
      <template v-if="queryAttributes.length">
        <ApiAttributes
          :attributes="queryAttributes"
          :title="t('parameters')"
        />
      </template>

      <slot name="left" />
    </div>

    <div :class="queryAttributes.length || $slots.left ? 'flex flex-col gap-6 order-1 xl:order-2' : 'flex gap-6 w-full flex-col xl:flex-row'">
      <template v-if="responseAttributes.length">
        <div class="w-full">
          <ApiAttributes
            :attributes="responseAttributes"
            :title="t('responseSchema')"
          />
        </div>
      </template>

      <div
        v-if="response || loading"
        class="w-full"
      >
        <ApiResponse
          v-if="response || loading"
          :level-to-open="levelToOpen"
          :loading="loading"
          max-height="55vh"
          :response="response"
        />
      </div>

      <slot name="right" />
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "parameters": "Parameters",
    "responseSchema": "Response schema"
  },
  "es": {
    "parameters": "Parámetros",
    "responseSchema": "Esquema de respuesta"
  },
  "fr": {
    "parameters": "Paramètres",
    "responseSchema": "Schéma de réponse"
  }
}
</i18n>

<style>
.api-docs-wrapper h1,
.api-docs-wrapper h2,
.api-docs-wrapper h3 {
  font-weight: 800;
  letter-spacing: -0.025em;
  text-transform: uppercase;
}

.api-docs-wrapper h1 {
  margin-bottom: 1.25rem;
  font-size: 1.875rem;
  line-height: 2.25rem;
}

.api-docs-wrapper h2 {
  margin-bottom: 1.25rem;
  font-size: 1.5rem;
  line-height: 2rem;
}

.api-docs-wrapper h3 {
  display: inline-block;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  margin-top: 0.25rem;
  margin-bottom: 1.5rem;
  font-size: 1rem;
  line-height: 1.5rem;
  border-radius: 0.375rem;
  border-width: 1px;
  border-color: #111827;
  box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06);
}

.api-docs-wrapper p:not(:last-child) {
  margin-bottom: 1.5rem;
}
</style>
